import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { renderSVG } from "uqr";
import { Copy, Download, FileText, Fingerprint, KeyRound, LogOut, MailCheck, ShieldCheck, ShieldOff } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { Badge, Button, Card, SectionTitle, TextField } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api, ApiError } from "@/lib/api";
import { useAction } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { LEGAL_DOCS } from "@shared/legal";
import type { AuditEntryDTO, MeDTO, SessionDTO } from "@shared/types";
import { DataCard } from "./More";
import { AddPasskeySheet } from "@/components/SwitchUser";
import { passkeysSupported, usePasskeys } from "@/lib/passkeys";

interface SecurityDTO {
  mfaEnabled: boolean;
  mfaEnabledAt: number | null;
  recoveryCodesLeft: number;
  emailVerified: boolean;
}

const when = (ts: number | null) =>
  ts ? new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

/** Settings → Privacy & security: kids' consent, two-step verification, sign-ins and the family's security activity. */
export function PrivacySecurity() {
  return (
    <div>
      <PageHeader title="Privacy & security" back="/parent/more" />
      <SectionTitle>Kids' privacy</SectionTitle>
      <ConsentCard />
      <SectionTitle>Two-step verification</SectionTitle>
      <MfaCard />
      <SectionTitle>Passkeys</SectionTitle>
      <PasskeysCard />
      <SectionTitle>Where you're signed in</SectionTitle>
      <SessionsCard />
      <SectionTitle>Security activity</SectionTitle>
      <ActivityCard />
      <SectionTitle>Policies</SectionTitle>
      <Card className="p-1.5">
        {Object.values(LEGAL_DOCS).map((d) => (
          <Link key={d.path} to={d.path} className="flex items-center gap-3 rounded-2xl px-3 py-3 hover:bg-sunken">
            <FileText className="size-5 text-brand" />
            <span className="flex-1 font-bold">{d.title}</span>
            <span className="text-sm text-muted">Version {d.version}</span>
          </Link>
        ))}
      </Card>
      <SectionTitle>Your data</SectionTitle>
      <DataCard />
    </div>
  );
}

/** Parental consent status, with resend / renew / withdraw. Also used on the home screen while consent is missing. */
export function ConsentCard({ compact = false }: { compact?: boolean }) {
  const { me, user } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [withdrawing, setWithdrawing] = useState(false);
  const [password, setPassword] = useState("");
  const close = useCallback(() => setWithdrawing(false), []);
  const status = me.privacy.consent;
  const resend = useAction(() => api.post<{ sentTo: string }>("/legal/consent/resend"), {
    onSuccess: (r) => toast.show(`Sent to ${r.sentTo}`, "success"),
  });
  const renew = useAction(() => api.post<MeDTO>("/legal/consent/renew", { agree: true }), {
    success: "Thanks! Kids can sign in again.",
    onSuccess: (m) => qc.setQueryData(["me"], m),
  });
  const withdraw = useAction(() => api.post<MeDTO>("/legal/consent/withdraw", { password }), {
    success: "Consent withdrawn. Kids were signed out.",
    onSuccess: (m) => {
      qc.setQueryData(["me"], m);
      setWithdrawing(false);
      setPassword("");
    },
  });

  if (status === "granted") {
    if (compact) return null;
    return (
      <Card className="space-y-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className="size-6 shrink-0 text-brand" />
          <div>
            <p className="font-extrabold">You've given consent for your kids</p>
            <p className="text-sm text-muted">Kids sign in with their PIN on devices you pair. We collect only what they do in the app.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/privacy#5-childrens-privacy-notice">
            <Button size="sm" variant="secondary">
              Children's privacy notice
            </Button>
          </Link>
          <Button size="sm" variant="ghost" className="text-danger" onClick={() => setWithdrawing(true)}>
            Withdraw consent…
          </Button>
        </div>
        <Sheet
          open={withdrawing}
          onClose={close}
          title="Withdraw consent?"
          footer={
            <Button block variant="danger" disabled={!password} loading={withdraw.isPending} onClick={() => withdraw.mutate()}>
              Withdraw and sign kids out
            </Button>
          }
        >
          <p className="text-sm text-muted">
            Your kids will be signed out right away and can't sign in until a parent gives consent again. Nothing is deleted: to erase a kid's
            information, delete their profile under Family &amp; kids.
          </p>
          <div className="mt-4">
            <TextField label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
        </Sheet>
      </Card>
    );
  }

  const renewal = status === "renewal_needed";
  return (
    <Card className="space-y-3 bg-warn-soft ring-0">
      <div className="flex items-start gap-3">
        <MailCheck className="mt-0.5 size-6 shrink-0 text-warn" />
        <div>
          <p className="font-extrabold">{renewal ? "Please review our updated children's notice" : "Kids can sign in once you give consent"}</p>
          <p className="text-sm">
            {renewal
              ? "We changed how we handle kids' information. Kids can't sign in until a parent reviews it and consents again."
              : `We emailed our children's privacy notice to ${user.email}. Open it and tap “Review and give consent”.`}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {renewal && me.privacy.emailVerified ? (
          <>
            <Link to="/privacy#5-childrens-privacy-notice">
              <Button size="sm" variant="secondary">
                Read the notice
              </Button>
            </Link>
            <Button size="sm" onClick={() => renew.mutate()} loading={renew.isPending}>
              I consent
            </Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => resend.mutate()} loading={resend.isPending}>
            Resend the email
          </Button>
        )}
      </div>
    </Card>
  );
}

function MfaCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const security = useQuery<SecurityDTO, ApiError>({ queryKey: ["security"], queryFn: () => api.get("/security") });
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [password, setPassword] = useState("");
  const qr = useMemo(() => (setup ? renderSVG(setup.otpauthUrl, { border: 1 }) : ""), [setup]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["security"] });
    void qc.invalidateQueries({ queryKey: ["me"] });
  };
  const start = useAction(() => api.post<{ secret: string; otpauthUrl: string }>("/security/mfa/setup"), { onSuccess: (s) => setSetup(s) });
  const enable = useAction(() => api.post<{ recoveryCodes: string[] }>("/security/mfa/enable", { code }), {
    onSuccess: (r) => {
      setSetup(null);
      setCode("");
      setCodes(r.recoveryCodes);
      refresh();
    },
  });
  const disable = useAction(() => api.post("/security/mfa/disable", { password, code }), {
    success: "Two-step verification is off",
    onSuccess: () => {
      setDisabling(false);
      setPassword("");
      setCode("");
      refresh();
    },
  });
  const newCodes = useAction((c: string) => api.post<{ recoveryCodes: string[] }>("/security/mfa/recovery-codes", { code: c }), {
    onSuccess: (r) => {
      setCodes(r.recoveryCodes);
      refresh();
    },
  });
  const closeSetup = useCallback(() => setSetup(null), []);
  const closeCodes = useCallback(() => setCodes(null), []);
  const closeDisable = useCallback(() => setDisabling(false), []);
  const s = security.data;
  if (!s) return <Card className="h-24 animate-pulse">{null}</Card>;

  async function copyCodes() {
    try {
      await navigator.clipboard.writeText(codes!.join("\n"));
      toast.show("Recovery codes copied", "success");
    } catch {
      toast.show("Couldn't copy: write them down instead.", "error");
    }
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-3">
        {s.mfaEnabled ? <ShieldCheck className="size-6 shrink-0 text-brand" /> : <ShieldOff className="size-6 shrink-0 text-muted" />}
        <div className="flex-1">
          <p className="font-extrabold">{s.mfaEnabled ? "On" : "Off"}</p>
          <p className="text-sm text-muted">
            {s.mfaEnabled
              ? `Signing in asks for a code from your authenticator app. ${s.recoveryCodesLeft} recovery codes left.`
              : "Protect your family's account: signing in will also ask for a code from an authenticator app on your phone."}
          </p>
        </div>
      </div>
      {s.mfaEnabled ? (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setDisabling(true)}>
            Turn off…
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const c = window.prompt("Enter a code from your authenticator app to make new recovery codes");
              if (c) newCodes.mutate(c);
            }}
          >
            <KeyRound className="size-4" /> New recovery codes
          </Button>
        </div>
      ) : (
        <Button onClick={() => start.mutate()} loading={start.isPending}>
          Turn on two-step verification
        </Button>
      )}

      <Sheet
        open={!!setup}
        onClose={closeSetup}
        title="Set up two-step verification"
        footer={
          <Button block disabled={code.length < 6} loading={enable.isPending} onClick={() => enable.mutate()}>
            Turn on
          </Button>
        }
      >
        {setup && (
          <div className="space-y-4 text-sm">
            <p>1. Open an authenticator app (Google Authenticator, 1Password, Authy, or your phone's password manager) and scan this code.</p>
            <div className="mx-auto w-48 rounded-2xl bg-white p-2" dangerouslySetInnerHTML={{ __html: qr }} />
            <p className="text-muted">
              On this phone? <a className="font-bold text-brand" href={setup.otpauthUrl}>Open in your authenticator app</a>, or enter this key:{" "}
              <code className="select-all break-all font-mono text-ink">{setup.secret}</code>
            </p>
            <p>2. Enter the 6-digit code it shows.</p>
            <TextField label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </div>
        )}
      </Sheet>

      <Sheet
        open={!!codes}
        onClose={closeCodes}
        title="Save your recovery codes"
        footer={
          <Button block onClick={() => setCodes(null)}>
            I've saved them
          </Button>
        }
      >
        {codes && (
          <div className="space-y-4">
            <p className="text-sm text-muted">If you lose your phone, each code lets you sign in once. Keep them somewhere safe; we won't show them again.</p>
            <ul className="grid grid-cols-2 gap-2 rounded-2xl bg-sunken p-4 font-mono text-lg font-bold">
              {codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={copyCodes}>
                <Copy className="size-4" /> Copy
              </Button>
              <a href={`data:text/plain;charset=utf-8,${encodeURIComponent(`Meaningful Rewards recovery codes\n\n${codes.join("\n")}\n`)}`} download="meaningful-rewards-recovery-codes.txt">
                <Button size="sm" variant="secondary">
                  <Download className="size-4" /> Download
                </Button>
              </a>
            </div>
          </div>
        )}
      </Sheet>

      <Sheet
        open={disabling}
        onClose={closeDisable}
        title="Turn off two-step verification?"
        footer={
          <Button block variant="danger" disabled={!password || code.length < 6} loading={disable.isPending} onClick={() => disable.mutate()}>
            Turn off
          </Button>
        }
      >
        <div className="space-y-4">
          <TextField label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <TextField label="Code from your app (or a recovery code)" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
      </Sheet>
    </Card>
  );
}

function PasskeysCard() {
  const qc = useQueryClient();
  const list = usePasskeys();
  const [adding, setAdding] = useState(false);
  const supported = passkeysSupported();
  const remove = useAction((id: string) => api.del<{ passkeys: unknown }>(`/security/passkeys/${encodeURIComponent(id)}`), {
    success: "Passkey removed",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passkeys"] }),
  });
  const passkeys = list.data?.passkeys ?? [];
  return (
    <Card className="space-y-3">
      <p className="text-sm text-muted">
        Sign in, or come back from a kid's view, with Face ID, your fingerprint or your phone's passcode instead of your password.
      </p>
      {passkeys.length > 0 && (
        <ul className="divide-y divide-line/70">
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center gap-3 py-2">
              <Fingerprint className="size-5 shrink-0 text-brand" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{p.label}</p>
                <p className="text-xs text-muted">
                  Added {new Date(p.createdAt).toLocaleDateString()}
                  {p.lastUsedAt ? ` · last used ${when(p.lastUsedAt)}` : ""}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-danger"
                onClick={() => window.confirm(`Remove the passkey on ${p.label}?`) && remove.mutate(p.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      {supported ? (
        <>
          <Button variant={passkeys.length ? "secondary" : "primary"} onClick={() => setAdding(true)}>
            <Fingerprint className="size-4" /> Add a passkey on this device
          </Button>
          <AddPasskeySheet open={adding} onClose={() => setAdding(false)} />
        </>
      ) : (
        <p className="text-sm text-muted">This browser can't make passkeys. Try Safari or Chrome on your phone.</p>
      )}
    </Card>
  );
}

function SessionsCard() {
  const list = useQuery<{ sessions: SessionDTO[] }, ApiError>({ queryKey: ["sessions"], queryFn: () => api.get("/security/sessions") });
  const qc = useQueryClient();
  const signOut = useAction((id: string) => api.del(`/security/sessions/${id}`), {
    success: "Signed out",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  const others = useAction(() => api.post("/security/sessions/sign-out-others"), {
    success: "Signed out everywhere else",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  const sessions = list.data?.sessions ?? [];
  return (
    <Card className="space-y-1 p-1.5">
      {sessions.map((s) => (
        <div key={s.id} className="flex items-center gap-3 rounded-2xl px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 font-bold">
              {s.device} {s.current && <Badge tone="brand">This device</Badge>}
            </p>
            <p className="text-sm text-muted">Last active {when(s.lastUsedAt)}</p>
          </div>
          {!s.current && (
            <Button size="sm" variant="ghost" onClick={() => signOut.mutate(s.id)}>
              Sign out
            </Button>
          )}
        </div>
      ))}
      {sessions.length > 1 && (
        <div className="px-3 pb-2 pt-1">
          <Button size="sm" variant="secondary" onClick={() => others.mutate()} loading={others.isPending}>
            <LogOut className="size-4" /> Sign out everywhere else
          </Button>
        </div>
      )}
    </Card>
  );
}

function ActivityCard() {
  const activity = useQuery<{ entries: AuditEntryDTO[] }, ApiError>({ queryKey: ["security-activity"], queryFn: () => api.get("/security/activity") });
  const [all, setAll] = useState(false);
  const entries = activity.data?.entries ?? [];
  if (!entries.length) return <Card className="text-sm text-muted">Nothing yet.</Card>;
  return (
    <Card className="space-y-2">
      <p className="text-sm text-muted">Devices, connected apps, consent and deletions in your family, kept for 12 months.</p>
      <ul className="divide-y divide-line/70">
        {(all ? entries : entries.slice(0, 8)).map((e) => (
          <li key={e.id} className="py-2 text-sm">
            <p>
              <b>{e.actor}</b>: {e.summary}
            </p>
            <p className="text-xs text-muted">{when(e.createdAt)}</p>
          </li>
        ))}
      </ul>
      {entries.length > 8 && !all && (
        <Button size="sm" variant="ghost" onClick={() => setAll(true)}>
          Show all
        </Button>
      )}
    </Card>
  );
}
